import { Button, Card, CardContent, Grid2, Popover, Stack, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import useTitle from 'utils/customHook/useTitle'
import i18n from 'simple-react-i18n'
import { CardTitle } from 'components/card/NewCard'
import PropTypes from 'prop-types'
import { Circle, CircleOutlined, ErrorOutline, SignalCellular0BarRounded, SignalCellular2BarRounded, SignalCellular3BarRounded, SignalCellular4BarRounded, Tune } from '@mui/icons-material'
import { round, orderBy, isEmpty, startsWith, without, includes, compact, values } from 'lodash'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import ProgressBar from 'components/progress/ProgressBar'
import { formatSign } from 'utils/NumberUtil'
import A3PCheckbox from './A3PComponent/A3PCheckbox'
import { push } from '@lagunovsky/redux-react-router'
import PopoverContent from './A3PComponent/PopoverContent'
import AgriAction from 'agriAdministration/actions/AgriAction'
import { getHydroSituationColor, getLevelLabel, getTrendIcon, getTrendLabel, trendEvolution } from 'exploitations/utils/AgriUtils'
import { CardTable } from 'components/datatable/NewTable'
import ProgressCard from 'components/card/ProgressCard'
import { getDarkerHex } from 'utils/ColorUtil'
import { darkBlue, errorColor } from 'utils/constants/ColorTheme'

const VPLevels = {
    RESTRICTED: 0,
    NORMAL: 1,
    HIGH: 2,
    VERY_HIGH: 3,
}

const riskOfShortageConst = {
    NULL: 0,
    UNLIKELY: 1,
    VERY_LIKELY: 2,
    CERTAIN: 3,
}

const climateConst = {
    DRY: 0,
    NORMAL: 1,
    DAMP: 2,
}

const NO_RESTRICTION = -1

const WhiteGrid = ({ size = 'auto', title, children }) => (
    <Grid2
        container
        size={size}
        direction={'column'}
        justifyContent={'space-between'}
        bgcolor={'mainWhite.main'}
        sx={{ padding: 1, borderRadius: 2 }}
    >
        <Typography sx={{ fontSize: '13px' }}>{title}</Typography>
        <Grid2 container spacing={0.5} alignItems={'center'}>
            {children}
        </Grid2>
    </Grid2>
)

WhiteGrid.propTypes = {
    children: PropTypes.node,
    title: PropTypes.string,
    size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

const HeaderComponent = ({ title, popover, popoverWidth = '250px' }) => {
    const [anchorPopover, setAnchorPopover] = useState(null)
    return (
        <Grid2
            container
            size={12}
            spacing={0.5}
            color={'white'}
            alignItems={'center'}
            justifyContent={'space-between'}
        >
            {popover ?
                <ErrorOutline
                    onClick={event => {
                        event.stopPropagation()
                        setAnchorPopover(anchorPopover ? null : event.currentTarget)
                    }}
                    sx={{
                        fontSize: '1.3rem',
                        '&:hover': {
                            cursor: 'pointer',
                        },
                    }}
                />
                :
                undefined
            }
            <Grid2
                size={'grow'}
                sx={{
                    fontWeight: 'bold',
                    fontSize: 12,
                    textWrap: 'wrap',
                    wordBreak: 'break-word',
                    textAlign: 'center',
                }}
            >
                {title}
            </Grid2>
            <Popover
                open={!!anchorPopover}
                anchorEl={anchorPopover}
                onClose={(event) => {
                    event.stopPropagation()
                    setAnchorPopover(null)
                }}
                onClick={(event) => event.stopPropagation()}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: 2,
                        },
                    },
                }}
            >
                <Grid2 container size={12} sx={{ padding: 1.5, width: popoverWidth }}>
                    {popover}
                    <Grid2
                        container
                        size={12}
                        justifyContent={'flex-end'}
                    >
                        <Button
                            onClick={() => setAnchorPopover(null)}
                            variant={'text'}
                            sx={{ textTransform: 'capitalize' }}
                        >
                            {i18n.close}
                        </Button>
                    </Grid2>
                </Grid2>
            </Popover>
        </Grid2>
    )
}

HeaderComponent.propTypes = {
    title: PropTypes.string,
    popover: PropTypes.element,
    popoverWidth: PropTypes.string,
}

const SummaryIndicator = ({ title, subTitle, content, secondaryContent, size = 'auto' }) => (
    <Grid2
        container
        size={size}
        justifyContent={'space-between'}
        direction={'column'}
        bgcolor={'primary.veryLight'}
        spacing={2}
        sx={{ padding: 1.5, borderRadius: 2 }}
    >
        <Grid2 container size={12} direction={'column'} spacing={0}>
            <Typography color={'third'} sx={{ fontSize: '14px', fontWeight: 'bold' }}>{title}</Typography>
            <Typography color={'third'} sx={{ fontSize: '11px' }}>{subTitle}</Typography>
        </Grid2>
        <Grid2 container size={12} spacing={1.5} alignItems={'center'}>
            {content && <Typography color={'third'} sx={{ fontSize: 25, fontWeight: 'bold' }}>{content}</Typography>}
            {secondaryContent}
        </Grid2>
    </Grid2>
)

SummaryIndicator.propTypes = {
    title: PropTypes.string.isRequired,
    subTitle: PropTypes.string,
    content: PropTypes.element,
    secondaryContent: PropTypes.element,
    size: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

const VPIndicator = ({ indicator }) => {
    const content = () => {
        switch (indicator) {
            case VPLevels.RESTRICTED:
                return (
                    <>
                        <SignalCellular0BarRounded />
                        {i18n.restricted}
                    </>
                )
            case VPLevels.HIGH:
                return (
                    <>
                        <SignalCellular3BarRounded />
                        {i18n.high}
                    </>
                )
            case VPLevels.VERY_HIGH:
                return (
                    <>
                        <SignalCellular4BarRounded />
                        {i18n.veryHigh}
                    </>
                )
            case VPLevels.NORMAL: default:
                return (
                    <>
                        <SignalCellular2BarRounded />
                        {i18n.normal}
                    </>
                )
        }
    }
    return (
        <Grid2
            container
            bgcolor={'primary.veryLight'}
            sx={{ padding: 0.75, borderRadius: 2 }}
            alignItems={'center'}
            spacing={1}
        >
            {content()}
        </Grid2>
    )
}

VPIndicator.propTypes = {
    indicator: PropTypes.number,
}

const RecommendationIndicator = ({ indicator }) => {
    switch (indicator) {
        case 0:
            return (
                <Grid2
                    container
                    color={getDarkerHex(errorColor, 50)}
                    bgcolor={`${getDarkerHex(errorColor, 50)}16`}
                    border={'solid 1px'}
                    borderColor={getDarkerHex(errorColor, 50)}
                    sx={{ padding: 0.75, margin: 1, borderRadius: 2 }}
                    alignItems={'center'}
                    justifyContent={'center'}
                >
                    {i18n.lower}
                </Grid2>
            )
        case 2:
            return (
                <Grid2
                    container
                    color={getDarkerHex(errorColor, 50)}
                    bgcolor={`${getDarkerHex(errorColor, 50)}16`}
                    border={'solid 1px'}
                    borderColor={getDarkerHex(errorColor, 50)}
                    sx={{ padding: 0.75, margin: 1, borderRadius: 2 }}
                    alignItems={'center'}
                    justifyContent={'center'}
                >
                    {i18n.increase}
                </Grid2>
            )
        case 1: default:
            return i18n.maintain
    }
}

RecommendationIndicator.propTypes = {
    indicator: PropTypes.number,
}

const TerritoryA3PApp = () => {
    const [dryScenario, setDryScenario] = useState(false)
    const [normalScenario, setNormalScenario] = useState(false)
    const [dampScenario, setDampScenario] = useState(false)
    const [dataLoaded, setDataLoaded] = useState(false)
    const [anchorClimatePopover, setAnchorClimatePopover] = useState(null)
    const [anchorFilterPopover, setAnchorFilterPopover] = useState(null)
    const [VPLevelsFilters, setVPLevelsFilters] = useState([])
    const [trajDeviationFilters, setTrajDeviationFilter] = useState('')
    const [alertLevelsFilters, setAlertLevelsFilters] = useState([])
    const [filters, setFilters] = useState({
        VPLevels: [], // Values from 0 to 3,
        trajDeviation: '', // 'neg' || 'pos' || 'all' || ''
        alertLevels: [], // contains restrictions IDs from alertLevels object
    })

    const {
        A3PDetails,
    } = useSelector(store => ({
        A3PDetails: store.AgriReducer.A3PDetails,
    }), shallowEqual)

    const dispatch = useDispatch()

    useTitle(() => [{
        title: i18n.steering,
        href: 'territory',
    }, {
        title: i18n.AUPSynthesis,
        href: 'territory/A3P',
    }], [])

    useEffect(() => {
        if (isEmpty(A3PDetails)) {
            dispatch(AgriAction.fetchA3PDatas({
                climateParams: {
                    dry: dryScenario,
                    normal: normalScenario,
                    damp: dampScenario,
                },
                onlyInRiskOfShortage: false,
            })).then(() => {
                setDataLoaded(true)
            })
        } else {
            setDataLoaded(true)
        }
    }, [])

    const {
        nbUgInRiskOfShortage,
        alertLevels,
        recommendedModif,
        climate,
        expectedYield,
        managementUnitsDetails = [],
    } = A3PDetails

    const getSitu = (situ) => {
        if (situ.restrictionColor || (situ.label !== 'Inconnu' && situ.color !== 'grey')) {
            const hexColor = startsWith(situ.color, '#') ? situ.color : getHydroSituationColor(situ.color)
            return (
                <Grid2 container size={12} spacing={0.5} alignItems={'center'} sx={{ padding: 0.5 }}>
                    <Grid2 container size='auto'>
                        {situ.restrictionColor ?
                            <Circle sx={{ color: situ.restrictionColor }} />
                            :
                            <CircleOutlined />
                        }
                    </Grid2>
                    <Grid2
                        container
                        size={'grow'}
                        alignItems='center'
                        justifyContent='center'
                        spacing={1}
                        sx={{
                            borderRadius: 1,
                            backgroundColor: `${hexColor}16`,
                            padding: 0.75,
                            fontSize: '11px',
                        }}
                    >
                        {situ.label !== 'Inconnu' &&
                            <Grid2 container size='auto'>{getTrendIcon(situ.label, situ.color)}</Grid2>
                        }
                        <Grid2
                            container
                            size='grow'
                            alignItems={'center'}
                            justifyContent={'center'}
                            sx={{ display: { xs: 'none', xl: 'flex' } }}
                        >
                            <Typography color={getDarkerHex(hexColor, 25)}>{getLevelLabel(situ.color)}</Typography>
                        </Grid2>
                    </Grid2>
                </Grid2>
            )
        }
        return '-'
    }

    const getClimateLabel = () => {
        switch (climate) {
            case climateConst.DRY: return i18n.dry
            case climateConst.DAMP: return i18n.damp
            case climateConst.NORMAL: default: return i18n.normal
        }
    }

    const getRiskOfShortageLabel = (riskOfShortage) => {
        switch (riskOfShortage) {
            case riskOfShortageConst.NULL: return i18n.null
            case riskOfShortageConst.UNLIKELY: return i18n.unlikely
            case riskOfShortageConst.VERY_LIKELY: return i18n.veryLikely
            case riskOfShortageConst.CERTAIN: return i18n.certain
            default: return i18n.unknown
        }
    }

    const filteredManagementUnitsDetails = useMemo(() => compact(managementUnitsDetails.filter(managementUnitDetail => {
        const formatedManagementUnitDetail = {
            ...managementUnitDetail,
            waterDispo: {
                ...managementUnitDetail.waterDispo,
                underground: {
                    ...managementUnitDetail.waterDispo.underground,
                    restrictionId: managementUnitDetail.waterDispo.underground.restrictionId ?? NO_RESTRICTION,
                },
                superficial: {
                    ...managementUnitDetail.waterDispo.superficial,
                    restrictionId: managementUnitDetail.waterDispo.superficial.restrictionId ?? NO_RESTRICTION,
                },
            },
        }
        const VPLevelFilterOK = filters.VPLevels.length ?
            includes(filters.VPLevels, formatedManagementUnitDetail.withdrawableVolume)
            :
            true
        if (!VPLevelFilterOK) return undefined

        const trajDeviationFilterOK = filters.trajDeviation && filters.trajDeviation !== 'all' ?
            (filters.trajDeviation === 'neg' ?
                formatedManagementUnitDetail.consumptionTrajectoryDeviation < 0
                :
                formatedManagementUnitDetail.consumptionTrajectoryDeviation >= 0)
            :
            true
        if (!trajDeviationFilterOK) return undefined

        const alertLevelsFilterOK = filters.alertLevels.length ?
            includes(filters.alertLevels, formatedManagementUnitDetail.waterDispo.underground.restrictionId) ||
            includes(filters.alertLevels, formatedManagementUnitDetail.waterDispo.superficial.restrictionId)
            :
            true
        if (!alertLevelsFilterOK) return undefined

        return managementUnitDetail
    })), [filters, managementUnitsDetails])

    const rows = useMemo(() => {
        return filteredManagementUnitsDetails.map(managementUnitDetail => {
            const situSout = getSitu(managementUnitDetail.waterDispo.underground)
            const situSup = getSitu(managementUnitDetail.waterDispo.superficial)
            return ({
                UGWithDefinition: managementUnitDetail.managementUnitName,
                esupAvailability: {
                    sortValue: managementUnitDetail.waterDispo.superficial.resultClass ?? NO_RESTRICTION,
                    value: situSup,
                    align: 'center',
                },
                esoutAvailability: {
                    sortValue: managementUnitDetail.waterDispo.underground.resultClass ?? NO_RESTRICTION,
                    value: situSout,
                    align: 'center',
                },
                percentVPConsumed: {
                    sortValue: managementUnitDetail.percentageAutorisationConsumed,
                    value:
                        <Grid2 container size={12} justifyContent={'center'} alignItems={'center'} spacing={2} >
                            <Grid2
                                size={'grow'}
                                sx={{ display: { xs: 'none', xl: 'block' } }}
                            >
                                <ProgressBar progress={managementUnitDetail.percentageAutorisationConsumed} />
                            </Grid2>
                            {`${round(managementUnitDetail.percentageAutorisationConsumed)} %`}
                        </Grid2>,
                },
                consumptionDeviation: {
                    sortValue: managementUnitDetail.consumptionTrajectoryDeviation,
                    value: `${formatSign(round(managementUnitDetail.consumptionTrajectoryDeviation, 1))} %`,
                    align: 'center',
                    fontColor: managementUnitDetail.consumptionTrajectoryDeviation <= -10 ?
                        'validStatus.contrastText'
                        :
                        managementUnitDetail.consumptionTrajectoryDeviation >= 10 ?
                            'errorStatus.contrastText'
                            :
                            undefined,
                },
                coverageNeeds: {
                    sortValue: managementUnitDetail.coverageRateOfNeeds,
                    value: `${round(managementUnitDetail.coverageRateOfNeeds, 1)} %`,
                    align: 'center',
                    fontColor: managementUnitDetail.coverageRateOfNeeds >= 100 ?
                        'validStatus.contrastText'
                        :
                        managementUnitDetail.coverageRateOfNeeds < 80 ?
                            'errorStatus.contrastText'
                            :
                            undefined,
                },
                riskOfShortage: {
                    sortValue: managementUnitDetail.riskOfShortage ?? -1,
                    value: getRiskOfShortageLabel(managementUnitDetail.riskOfShortage),
                    align: 'center',
                    fontColor: managementUnitDetail.riskOfShortage >= riskOfShortageConst.VERY_LIKELY ?
                        'errorStatus.contrastText'
                        :
                        undefined,
                },
                withdrawableVolume: {
                    sortValue: managementUnitDetail.withdrawableVolume,
                    value:
                        <Grid2 container padding={1} justifyContent={'center'}>
                            <VPIndicator indicator={managementUnitDetail.withdrawableVolume} />
                        </Grid2>,
                },
                advise: {
                    sortValue: managementUnitDetail.recommendation,
                    value: <RecommendationIndicator indicator={managementUnitDetail.recommendation} />,
                    align: 'center',
                },
                onClickValue: managementUnitDetail.managementCode,
            })
        })
    }, [filteredManagementUnitsDetails])

    if (!dataLoaded) return <ProgressCard indeterminate />

    if (isEmpty(A3PDetails)) return undefined

    return (
        <Grid2 container spacing={1.5} padding={2}>
            <Grid2 size={12} sx={{ position: 'sticky', top: '64px' }}>
                <Card>
                    <CardTitle title={i18n.climateControlScenario} actions={[
                        <ErrorOutline
                            key={0}
                            onClick={event => {
                                event.stopPropagation()
                                setAnchorClimatePopover(anchorClimatePopover ? null : event.currentTarget)
                            }}
                            sx={{
                                fontSize: '25px',
                                '&:hover': {
                                    cursor: 'pointer',
                                },
                            }}
                        />]}
                    />
                    <Popover
                        open={!!anchorClimatePopover}
                        anchorEl={anchorClimatePopover}
                        onClose={(event) => {
                            event.stopPropagation()
                            setAnchorClimatePopover(null)
                        }}
                        onClick={(event) => event.stopPropagation()}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'center',
                        }}
                        slotProps={{
                            paper: {
                                sx: {
                                    borderRadius: '16px',
                                },
                            },
                        }}
                    >
                        <Grid2 container size={12} sx={{ padding: 1.5, width: '250px' }}>
                            <PopoverContent explaination={i18n.climateControlScenarioExplaination}/>
                            <Grid2
                                container
                                size={12}
                                justifyContent={'flex-end'}
                            >
                                <Button
                                    onClick={() => setAnchorClimatePopover(null)}
                                    variant={'text'}
                                    sx={{ textTransform: 'capitalize' }}
                                >
                                    {i18n.close}
                                </Button>
                            </Grid2>
                        </Grid2>
                    </Popover>
                    <CardContent>
                        <Grid2 container size={12} alignItems={'center'} spacing={1.5}>
                            <A3PCheckbox label={i18n.dry} checked={dryScenario} onChange={isChecked => setDryScenario(isChecked)} />
                            <A3PCheckbox label={i18n.normal} checked={normalScenario} onChange={isChecked => setNormalScenario(isChecked)} />
                            <A3PCheckbox label={i18n.damp} checked={dampScenario} onChange={isChecked => setDampScenario(isChecked)} />
                        </Grid2>
                    </CardContent>
                </Card>
            </Grid2>
            <Grid2 size={12}>
                <Card>
                    <CardTitle title={i18n.indicatorsByManagementUnitSynthesis} actions={[]}/>
                    <CardContent>
                        <Grid2 container size={12} alignItems={'stretch'} spacing={1.5}>
                            <SummaryIndicator
                                size={{ xs: 3, xl: 1.5 }}
                                title={i18n.UGAtRiskOfShortage}
                                content={
                                    <Grid2 container alignItems={'baseline'}>
                                        <Typography sx={{ fontSize: '40px', fontWeight: 'bold' }}>{nbUgInRiskOfShortage}</Typography>
                                        {`/${managementUnitsDetails.length}`}
                                    </Grid2>
                                }
                            />
                            <SummaryIndicator
                                size={{ xs: 3, xl: 2 }}
                                title={i18n.recommendedChanges}
                                secondaryContent={
                                    <Grid2 container size={12} spacing={1}>
                                        <WhiteGrid size={6} title={i18n.reduct}>
                                            <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>{recommendedModif.reduce}</Typography>
                                        </WhiteGrid>
                                        <WhiteGrid size={6} title={i18n.increase}>
                                            <Typography sx={{ fontSize: '20px', fontWeight: 'bold' }}>{recommendedModif.increase}</Typography>
                                        </WhiteGrid>
                                    </Grid2>
                                }
                            />
                            <SummaryIndicator
                                size={{ xs: 3, xl: 2 }}
                                title={i18n.climate}
                                subTitle={i18n.observedOverTheLast3Months}
                                content={
                                    <Grid2>{getClimateLabel()}</Grid2>
                                }
                            />
                            <SummaryIndicator
                                size={{ xs: 3, xl: 2 }}
                                title={i18n.expectedYield}
                                subTitle={i18n.ofIrrigatedCorn}
                                content={
                                    <Grid2 container alignItems={'baseline'}>
                                        <Typography sx={{ fontSize: '40px', fontWeight: 'bold' }}>{expectedYield}</Typography>
                                        q/Ha
                                    </Grid2>
                                }
                            />
                            <SummaryIndicator
                                size={{ xs: 12, xl: 4.5 }}
                                title={i18n.warningsLevel}
                                secondaryContent={
                                    <Grid2 container size={12} spacing={1}>
                                        {orderBy(alertLevels, 'idAlert', 'asc').map((restriction, i) => {
                                            if (restriction.idAlert < 0) {
                                                return (
                                                    <WhiteGrid
                                                        key={i}
                                                        size={12/(alertLevels.length)}
                                                        title={restriction.alertLabel}
                                                    >
                                                        <CircleOutlined />
                                                        <Grid2 size={'grow'} sx={{ fontSize: 20, fontWeight: 'bold' }}>
                                                            {restriction.nbUg}
                                                        </Grid2>
                                                    </WhiteGrid>
                                                )
                                            }
                                            return (
                                                <WhiteGrid
                                                    key={i}
                                                    size={12/(alertLevels.length)}
                                                    title={restriction.alertLabel}
                                                >
                                                    <Circle sx={{ color: restriction.alertColor }} />
                                                    <Grid2 size={'grow'} sx={{ fontSize: 20, fontWeight: 'bold' }}>
                                                        {restriction.nbUg}
                                                    </Grid2>
                                                </WhiteGrid>
                                            )
                                        })}
                                    </Grid2>
                                }
                            />
                        </Grid2>
                    </CardContent>
                </Card>
            </Grid2>
            <Grid2
                size={12}
                sx={{
                    position: 'sticky',
                    top: '190px',
                    maxHeight: 'calc(100vh - 206.5px)',
                    overflow: 'hidden',
                }}
            >
                <CardTable
                    title={i18n.detailByUG}
                    actions={[
                        <Button
                            key={0}
                            onClick={(event) => {
                                event.stopPropagation()
                                setAnchorFilterPopover(anchorFilterPopover ? null : event.currentTarget)
                            }}
                            variant={'outlined'}
                            color='mainBlack'
                            sx={{ textTransform: 'capitalize', padding: '2px 8px' }}
                            startIcon={<Tune />}
                        >{i18n.filters}</Button>,
                    ]}
                    maxHeight={'calc(100vh - 298px)'}
                    onClickRow={(row) => dispatch(push(`/territory/A3P/${row.onClickValue}`))}

                    rows={rows}
                    headers={[
                        {
                            key: 'UGWithDefinition',
                            color: darkBlue,
                            paddingBottom: 1,
                            value: <HeaderComponent title={i18n.UGWithDefinition} />,
                        },
                        {
                            key: 'esupAvailability',
                            color: darkBlue,
                            paddingBottom: 1,
                            width: { xs: 'auto', xl: '180px' },
                            value: <HeaderComponent
                                title={i18n.esupAvailability}
                                popover={
                                    <PopoverContent
                                        explaination={i18n.basedOnVCN3}
                                        legend={{
                                            keys: [
                                                i18n.trend,
                                                i18n.level,
                                            ],
                                            label: [
                                                <Stack key={0} direction={'row'} spacing={1}>
                                                    {values(trendEvolution).map((label, i) => (
                                                        <Grid2
                                                            key={i}
                                                            container
                                                            size='grow'
                                                            alignItems='center'
                                                            justifyContent='center'
                                                            spacing={1}
                                                            sx={{
                                                                borderRadius: 1,
                                                                backgroundColor: `${getHydroSituationColor('grey')}16`,
                                                                padding: 0.75,
                                                                fontSize: '11px',
                                                            }}
                                                        >
                                                            <Grid2 container size='auto'>{getTrendIcon(label, 'grey')}</Grid2>
                                                            <Grid2
                                                                container
                                                                size='grow'
                                                                alignItems={'center'}
                                                                justifyContent={'center'}
                                                            >
                                                                <Typography color={getDarkerHex(getHydroSituationColor('grey'), 25)}>
                                                                    {getTrendLabel(label)}
                                                                </Typography>
                                                            </Grid2>
                                                        </Grid2>
                                                    ))}
                                                </Stack>,
                                                <Grid2 container size={12} key={1} spacing={1}>
                                                    {['darkblue', 'yellow', 'deepskyblue', 'orange', 'darkgreen', 'red', 'limegreen'].map((color, i) => (
                                                        <Grid2
                                                            key={i}
                                                            container
                                                            size={6}
                                                            alignItems='center'
                                                            justifyContent='center'
                                                            spacing={1}
                                                            sx={{
                                                                borderRadius: 1,
                                                                backgroundColor: `${getHydroSituationColor(color)}16`,
                                                                padding: 0.75,
                                                                fontSize: '11px',
                                                            }}
                                                        >
                                                            <Grid2 container size='auto'>{getTrendIcon('right', color)}</Grid2>
                                                            <Grid2
                                                                container
                                                                size='grow'
                                                                alignItems={'center'}
                                                                justifyContent={'center'}
                                                            >
                                                                <Typography color={getDarkerHex(getHydroSituationColor(color), 25)}>
                                                                    {getLevelLabel(color)}
                                                                </Typography>
                                                            </Grid2>
                                                        </Grid2>
                                                    ))}
                                                </Grid2>,
                                            ],
                                        }}
                                    />
                                }
                                popoverWidth={'360px'}
                            />,
                        },
                        {
                            key: 'esoutAvailability',
                            color: darkBlue,
                            paddingBottom: 1,
                            width: { xs: 'auto', xl: '180px' },
                            value: <HeaderComponent
                                title={i18n.esoutAvailability}
                                popover={
                                    <PopoverContent
                                        explaination={i18n.basedOnIPS}
                                        legend={{
                                            keys: [
                                                i18n.trend,
                                                i18n.level,
                                            ],
                                            label: [
                                                <Stack key={0} direction={'row'} spacing={1}>
                                                    {values(trendEvolution).map((label, i) => (
                                                        <Grid2
                                                            key={i}
                                                            container
                                                            size='grow'
                                                            alignItems='center'
                                                            justifyContent='center'
                                                            spacing={1}
                                                            sx={{
                                                                borderRadius: 1,
                                                                backgroundColor: `${getHydroSituationColor('grey')}16`,
                                                                padding: 0.75,
                                                                fontSize: '11px',
                                                            }}
                                                        >
                                                            <Grid2 container size='auto'>{getTrendIcon(label, 'grey')}</Grid2>
                                                            <Grid2
                                                                container
                                                                size='grow'
                                                                alignItems={'center'}
                                                                justifyContent={'center'}
                                                            >
                                                                <Typography
                                                                    color={getDarkerHex(getHydroSituationColor('grey'), 25)}
                                                                >
                                                                    {getTrendLabel(label)}
                                                                </Typography>
                                                            </Grid2>
                                                        </Grid2>
                                                    ))}
                                                </Stack>,
                                                <Grid2 container size={12} key={1} spacing={1}>
                                                    {['#4400F6', '#F7FF28', '#3E66ED', '#F6BD1B', '#34FFFB', '#F21007', '#48FF24'].map((color, i) => (
                                                        <Grid2
                                                            key={i}
                                                            container
                                                            size={6}
                                                            alignItems='center'
                                                            justifyContent='center'
                                                            spacing={1}
                                                            sx={{
                                                                borderRadius: 1,
                                                                backgroundColor: `${color}16`,
                                                                padding: 0.75,
                                                                fontSize: '11px',
                                                            }}
                                                        >
                                                            <Grid2 container size='auto'>{getTrendIcon('right', color)}</Grid2>
                                                            <Grid2
                                                                container
                                                                size='grow'
                                                                alignItems={'center'}
                                                                justifyContent={'center'}
                                                            >
                                                                <Typography
                                                                    color={getDarkerHex(color, 25)}
                                                                >
                                                                    {getLevelLabel(color)}
                                                                </Typography>
                                                            </Grid2>
                                                        </Grid2>
                                                    ))}
                                                </Grid2>,
                                            ],
                                        }}
                                    />
                                }
                                popoverWidth={'360px'}
                            />,
                        },
                        {
                            key: 'percentVPConsumed',
                            color: darkBlue,
                            paddingBottom: 1,
                            value: <HeaderComponent
                                title={i18n.percentVPConsumed}
                                popover={
                                    <PopoverContent
                                        explaination={i18n.percentVPConsumedExplainationText}
                                    />
                                }
                            />,
                        },
                        {
                            key: 'consumptionDeviation',
                            color: darkBlue,
                            paddingBottom: 1,
                            value: <HeaderComponent
                                title={i18n.consumptionDeviation}
                                popover={
                                    <PopoverContent
                                        explaination={i18n.consumptionDeviationExplainationText}
                                        legend={{
                                            keys: [
                                                { label: i18n.belowMinus10Percent, color: 'validStatus.contrastText' },
                                                i18n.fromMinus10To5Percent,
                                                i18n.from5To10Percent,
                                                { label: i18n.moreThan10Percent, color: 'errorStatus.contrastText' },
                                            ],
                                            label: [
                                                i18n.consoLowerThanForecast,
                                                i18n.consoCloseToForecast,
                                                i18n.consoHigherThanForecast,
                                                i18n.consoSignificantlyHigherThanForecast,
                                            ],
                                        }}
                                    />
                                }
                            />,
                        },
                        {
                            key: 'coverageNeeds',
                            color: darkBlue,
                            paddingBottom: 1,
                            value: <HeaderComponent
                                title={i18n.coverageNeeds}
                                popover={
                                    <PopoverContent
                                        explaination={i18n.coverageNeedsExplainationText}
                                        legend={{
                                            keys: [
                                                { label: i18n.superiorOrEqualTo100Percent, color: 'validStatus.contrastText' },
                                                i18n.from80To99Percent,
                                                { label: i18n.below80Percent, color: 'errorStatus.contrastText' },
                                            ],
                                            label: [
                                                i18n.needsFullyCoveredOrExceeded,
                                                i18n.resourcesPartiallyInsufficientRequireVigilance,
                                                i18n.resourcesSignificantlyInsufficientHighRiskOfDeficit,
                                            ],
                                        }}
                                    />
                                }
                            />,
                        },
                        {
                            key: 'riskOfShortage',
                            color: darkBlue,
                            paddingBottom: 1,
                            value: <HeaderComponent
                                title={i18n.riskOfShortage}
                                popover={
                                    <PopoverContent
                                        explaination={i18n.riskOfShortageExplainationText}
                                        legend={{
                                            keys: [
                                                i18n.unknown,
                                                i18n.null,
                                                i18n.unlikely,
                                                { label: i18n.veryLikely, color: 'errorStatus.contrastText' },
                                                { label: i18n.certain, color: 'errorStatus.contrastText' },
                                            ],
                                            label: [
                                                i18n.notEnoughShortageDatas,
                                                i18n.noRiskIdentified,
                                                i18n.lowRiskConfortableSituation,
                                                i18n.highRiskNearCriticalThreshold,
                                                i18n.thresholdExceededExpectedShortage,
                                            ],
                                        }}
                                    />
                                }
                            />,
                        },
                        {
                            key: 'withdrawableVolume',
                            color: darkBlue,
                            paddingBottom: 1,
                            value: <HeaderComponent
                                title={i18n.withdrawableVolume}
                                popover={
                                    <PopoverContent
                                        explaination={i18n.VPExplainationText}
                                        legend={{
                                            keys: [
                                                <VPIndicator key={0} indicator={0} />,
                                                <VPIndicator key={1} indicator={1} />,
                                                <VPIndicator key={2} indicator={2} />,
                                                <VPIndicator key={3} indicator={3} />,
                                            ],
                                            label: [
                                                i18n.restrictedVPExplaination,
                                                i18n.normalVPExplaination,
                                                i18n.highVPExplaination,
                                                i18n.veryHighVPExplaination,
                                            ],
                                        }}
                                    />
                                }
                            />,
                        },
                        {
                            key: 'advise',
                            color: darkBlue,
                            paddingBottom: 1,
                            value: <HeaderComponent title={i18n.advise} popover={<PopoverContent explaination={i18n.adviseExplainationText} />} />,
                        },
                    ]}
                />
            </Grid2>
            <Popover
                open={!!anchorFilterPopover}
                anchorEl={anchorFilterPopover}
                onClose={(event) => {
                    event.stopPropagation()
                    setAnchorFilterPopover(null)
                }}
                onClick={(event) => event.stopPropagation()}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                slotProps={{
                    paper: {
                        sx: {
                            borderRadius: '16px',
                        },
                    },
                }}
            >
                <Grid2 container size={12} sx={{ padding: 1.5, width: '600px' }}>
                    <PopoverContent legend={{
                        keys: [
                            i18n.VPLevel,
                            i18n.trajDeviation,
                            i18n.warningsLevel,
                        ],
                        label: [
                            <Grid2 key={0} container size={12} spacing={1} paddingBottom={1}>
                                <A3PCheckbox
                                    label={i18n.restricted}
                                    sx={{ padding: 1 }}
                                    onChange={(isChecked) => {
                                        setVPLevelsFilters((prev) =>
                                            isChecked ?
                                                [ ...prev, VPLevels.RESTRICTED ]
                                                :
                                                without(prev, VPLevels.RESTRICTED),
                                        )
                                    }}
                                    checked={includes(VPLevelsFilters, VPLevels.RESTRICTED)}
                                />
                                <A3PCheckbox
                                    label={i18n.normal}
                                    sx={{ padding: 1 }}
                                    onChange={(isChecked) => {
                                        setVPLevelsFilters((prev) =>
                                            isChecked ?
                                                [ ...prev, VPLevels.NORMAL ]
                                                :
                                                without(prev, VPLevels.NORMAL),
                                        )
                                    }}
                                    checked={includes(VPLevelsFilters, VPLevels.NORMAL)}
                                />
                                <A3PCheckbox
                                    label={i18n.high}
                                    sx={{ padding: 1 }}
                                    onChange={(isChecked) => {
                                        setVPLevelsFilters((prev) =>
                                            isChecked ?
                                                [ ...prev, VPLevels.HIGH ]
                                                :
                                                without(prev, VPLevels.HIGH),
                                        )
                                    }}
                                    checked={includes(VPLevelsFilters, VPLevels.HIGH)}
                                />
                                <A3PCheckbox
                                    label={i18n.veryHigh}
                                    sx={{ padding: 1 }}
                                    onChange={(isChecked) => {
                                        setVPLevelsFilters((prev) =>
                                            isChecked ?
                                                [ ...prev, VPLevels.VERY_HIGH ]
                                                :
                                                without(prev, VPLevels.VERY_HIGH),
                                        )
                                    }}
                                    checked={includes(VPLevelsFilters, VPLevels.VERY_HIGH)}
                                />
                            </Grid2>,
                            <Grid2 key={1} container size={12} spacing={1} paddingBottom={1}>
                                <A3PCheckbox
                                    label={i18n.negative}
                                    sx={{ padding: 1 }}
                                    onChange={(isChecked) => setTrajDeviationFilter((prev) => {
                                        if (isChecked) {
                                            switch (prev) {
                                                case 'pos':
                                                    return 'all'
                                                case '': default:
                                                    return 'neg'
                                            }
                                        }
                                        switch (prev) {
                                            case 'all':
                                                return 'pos'
                                            case 'neg': default:
                                                return ''
                                        }
                                    })}
                                    checked={trajDeviationFilters === 'neg' || trajDeviationFilters === 'all'}
                                />
                                <A3PCheckbox
                                    label={i18n.positive}
                                    sx={{ padding: 1 }}
                                    onChange={(isChecked) => setTrajDeviationFilter((prev) => {
                                        if (isChecked) {
                                            switch (prev) {
                                                case 'neg':
                                                    return 'all'
                                                case '': default:
                                                    return 'pos'
                                            }
                                        }
                                        switch (prev) {
                                            case 'all':
                                                return 'neg'
                                            case 'pos': default:
                                                return ''
                                        }
                                    })}
                                    checked={trajDeviationFilters === 'pos' || trajDeviationFilters === 'all'}
                                />
                            </Grid2>,
                            <Grid2 key={2} container size={12} spacing={1}>
                                {orderBy(alertLevels, 'idAlert', 'asc').map((restriction, i) => (
                                    <A3PCheckbox
                                        key={i}
                                        label={restriction.alertLabel}
                                        sx={{ padding: 1 }}
                                        onChange={(isChecked) => {
                                            setAlertLevelsFilters((prev) =>
                                                isChecked ?
                                                    [ ...prev, restriction.idAlert ]
                                                    :
                                                    without(prev, restriction.idAlert),
                                            )
                                        }}
                                        checked={includes(alertLevelsFilters, restriction.idAlert)}
                                    />
                                ))}
                            </Grid2>,
                        ],
                    }}
                    />
                    <Grid2 container size={12} justifyContent={'flex-end'} spacing={1}>
                        <Button
                            onClick={() => {
                                setVPLevelsFilters([])
                                setTrajDeviationFilter('')
                                setAlertLevelsFilters([])
                            }}
                            variant={'outlined'}
                            sx={{ textTransform: 'capitalize' }}
                        >
                            {i18n.reinit}
                        </Button>
                        <Button
                            onClick={() => {
                                setFilters({
                                    VPLevels: VPLevelsFilters,
                                    trajDeviation: trajDeviationFilters,
                                    alertLevels: alertLevelsFilters,
                                })
                                setAnchorFilterPopover(null)
                            }}
                            variant={'contained'}
                            sx={{ textTransform: 'capitalize' }}
                        >
                            {i18n.apply}
                        </Button>
                    </Grid2>
                </Grid2>
            </Popover>
        </Grid2>
    )
}

export default TerritoryA3PApp